<template>
    <!-- Confirm Modal -->
    <ConfirmModal
        :show="confirmModal.show"
        :title="confirmModal.title"
        :message="confirmModal.message"
        :type="confirmModal.type"
        :confirmText="confirmModal.confirmText"
        @confirm="confirmModal.onConfirm"
        @cancel="confirmModal.show = false"
    />

    <div class="h-full bg-gray-50 dark:bg-gray-900 overflow-y-auto p-6">
        <div class="max-w-4xl mx-auto">
            <!-- Preview Mode -->
            <div v-if="previewMode" class="space-y-6">
                <div v-for="section in localSections" :key="section.id" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                    <div class="mb-6">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ section.title || 'Untitled Section' }}</h2>
                        <p v-if="section.description" class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ section.description }}</p>
                    </div>

                    <div class="space-y-6">
                        <div v-for="field in getVisibleFields(section)" :key="field.id" class="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                            <!-- Field Label -->
                            <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                {{ field.label || 'Untitled Field' }}
                                <span v-if="isFieldRequired(field)" class="text-red-500">*</span>
                            </label>
                            <p v-if="field.help_text" class="text-xs text-gray-500 dark:text-gray-400 mb-3">{{ field.help_text }}</p>

                            <!-- Field Input Preview -->
                            <div class="mb-4">
                                <!-- Yes/No -->
                                <div v-if="field.field_type === 'yes_no'" class="flex space-x-3">
                                    <button
                                        @click="updateFieldValue(field.id, 'yes')"
                                        type="button"
                                        :class="[
                                            'px-4 py-2 border rounded-md transition-colors',
                                            previewValues[field.id] === 'yes'
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                                        ]"
                                    >
                                        Yes
                                    </button>
                                    <button
                                        @click="updateFieldValue(field.id, 'no')"
                                        type="button"
                                        :class="[
                                            'px-4 py-2 border rounded-md transition-colors',
                                            previewValues[field.id] === 'no'
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                                        ]"
                                    >
                                        No
                                    </button>
                                </div>

                                <!-- Pass/Fail/NA -->
                                <div v-else-if="field.field_type === 'pass_fail_na'" class="flex space-x-3">
                                    <button
                                        @click="updateFieldValue(field.id, 'pass')"
                                        type="button"
                                        :class="[
                                            'px-4 py-2 border rounded-md transition-colors',
                                            previewValues[field.id] === 'pass'
                                                ? 'bg-green-600 border-green-600 text-white'
                                                : 'border-gray-300 dark:border-gray-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
                                        ]"
                                    >
                                        Pass
                                    </button>
                                    <button
                                        @click="updateFieldValue(field.id, 'fail')"
                                        type="button"
                                        :class="[
                                            'px-4 py-2 border rounded-md transition-colors',
                                            previewValues[field.id] === 'fail'
                                                ? 'bg-red-600 border-red-600 text-white'
                                                : 'border-gray-300 dark:border-gray-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                                        ]"
                                    >
                                        Fail
                                    </button>
                                    <button
                                        @click="updateFieldValue(field.id, 'na')"
                                        type="button"
                                        :class="[
                                            'px-4 py-2 border rounded-md transition-colors',
                                            previewValues[field.id] === 'na'
                                                ? 'bg-gray-600 border-gray-600 text-white'
                                                : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                                        ]"
                                    >
                                        N/A
                                    </button>
                                </div>

                                <!-- Multiple Choice -->
                                <div v-else-if="field.field_type === 'multiple_choice'" class="space-y-2">
                                    <div v-for="(option, idx) in getFieldOptions(field)" :key="idx" class="flex items-center">
                                        <input
                                            type="radio"
                                            :name="`field-${field.id}`"
                                            :value="option"
                                            v-model="previewValues[field.id]"
                                            @change="updateFieldValue(field.id, option)"
                                            class="mr-2 text-blue-600"
                                        />
                                        <label class="text-sm text-gray-900 dark:text-white">{{ option }}</label>
                                    </div>
                                </div>

                                <!-- Multiple Selection -->
                                <div v-else-if="field.field_type === 'multiple_selection'" class="space-y-2">
                                    <div v-for="(option, idx) in getFieldOptions(field)" :key="idx" class="flex items-center">
                                        <input
                                            type="checkbox"
                                            :value="option"
                                            v-model="previewValues[field.id]"
                                            class="mr-2 text-blue-600 rounded"
                                        />
                                        <label class="text-sm text-gray-900 dark:text-white">{{ option }}</label>
                                    </div>
                                </div>

                                <!-- Dropdown -->
                                <select
                                    v-else-if="field.field_type === 'dropdown_select'"
                                    v-model="previewValues[field.id]"
                                    @change="updateFieldValue(field.id, previewValues[field.id])"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select an option...</option>
                                    <option v-for="(option, idx) in getFieldOptions(field)" :key="idx" :value="option">{{ option }}</option>
                                </select>

                                <!-- Short Text -->
                                <input
                                    v-else-if="field.field_type === 'text_input'"
                                    type="text"
                                    v-model="previewValues[field.id]"
                                    placeholder="Enter text..."
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />

                                <!-- Long Text -->
                                <textarea
                                    v-else-if="field.field_type === 'textarea'"
                                    v-model="previewValues[field.id]"
                                    rows="3"
                                    placeholder="Enter detailed response..."
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                ></textarea>

                                <!-- Number -->
                                <div v-else-if="field.field_type === 'number_input'" class="flex items-center space-x-2">
                                    <input
                                        type="number"
                                        v-model="previewValues[field.id]"
                                        placeholder="0"
                                        class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <span v-if="getFieldConfig(field)?.unit" class="text-sm text-gray-600 dark:text-gray-400">{{ getFieldConfig(field).unit }}</span>
                                </div>

                                <!-- Decimal -->
                                <div v-else-if="field.field_type === 'decimal_input'" class="flex items-center space-x-2">
                                    <input
                                        type="number"
                                        step="0.01"
                                        v-model="previewValues[field.id]"
                                        placeholder="0.00"
                                        class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <span v-if="getFieldConfig(field)?.unit" class="text-sm text-gray-600 dark:text-gray-400">{{ getFieldConfig(field).unit }}</span>
                                </div>

                                <!-- Rating -->
                                <div v-else-if="field.field_type === 'rating_scale'" class="flex space-x-1">
                                    <button
                                        v-for="i in (getFieldConfig(field)?.max || 5)"
                                        :key="i"
                                        type="button"
                                        @click="updateFieldValue(field.id, i)"
                                        class="transition-colors focus:outline-none"
                                    >
                                        <StarIcon
                                            v-if="previewValues[field.id] >= i"
                                            class="w-8 h-8 text-yellow-400"
                                        />
                                        <StarIconOutline
                                            v-else
                                            class="w-8 h-8 text-gray-300 dark:text-gray-600 hover:text-yellow-200"
                                        />
                                    </button>
                                </div>

                                <!-- Date -->
                                <input
                                    v-else-if="field.field_type === 'date_picker'"
                                    type="date"
                                    v-model="previewValues[field.id]"
                                    class="w-48 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />

                                <!-- DateTime -->
                                <input
                                    v-else-if="field.field_type === 'datetime_picker'"
                                    type="datetime-local"
                                    v-model="previewValues[field.id]"
                                    class="w-64 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />

                                <!-- Time -->
                                <input
                                    v-else-if="field.field_type === 'time_picker'"
                                    type="time"
                                    v-model="previewValues[field.id]"
                                    class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />

                                <!-- Photo Capture -->
                                <div v-else-if="field.field_type === 'photo_capture'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                    <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">Tap to capture photo</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">Live camera capture only</p>
                                </div>

                                <!-- File Upload -->
                                <div v-else-if="field.field_type === 'file_upload'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                    <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">Upload file</p>
                                </div>

                                <!-- Signature -->
                                <div v-else-if="field.field_type === 'signature'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-white dark:bg-gray-700">
                                    <p class="text-sm text-gray-600 dark:text-gray-400 text-center mb-2">Sign here</p>
                                    <div class="h-24 bg-gray-50 dark:bg-gray-800 rounded"></div>
                                </div>
                            </div>

                            <!-- Field Supplements -->
                            <div class="space-y-3">
                                <!-- Notes -->
                                <div v-if="field.allow_notes" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        📝 Notes/Comments {{ field.require_notes ? '*' : '(Optional)' }}
                                    </label>
                                    <textarea
                                        v-model="previewValues[`${field.id}_notes`]"
                                        rows="2"
                                        placeholder="Add notes..."
                                        class="w-full text-sm rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    ></textarea>
                                </div>

                                <!-- Photos -->
                                <div v-if="field.allow_photos" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        📷 Photos {{ field.require_photos ? '*' : '(Optional)' }}
                                    </label>
                                    <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                                        <svg class="w-8 h-8 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <p class="text-xs text-gray-600 dark:text-gray-400">Take photo</p>
                                    </div>
                                </div>

                                <!-- Attachments -->
                                <div v-if="field.allow_attachments" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        📎 Attachments {{ field.require_attachments ? '*' : '(Optional)' }}
                                    </label>
                                    <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                                        <svg class="w-8 h-8 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        <p class="text-xs text-gray-600 dark:text-gray-400">Add attachments</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="!localSections || localSections.length === 0" class="text-center py-12 text-gray-400 dark:text-gray-500">
                    <p>No sections added yet. Switch to Edit mode to start building your checklist.</p>
                </div>
            </div>

            <!-- Edit Mode -->
            <div v-else>
                <!-- Sections -->
                <draggable
                    v-model="localSections"
                    item-key="id"
                    handle=".section-handle"
                    @end="onSectionReorder"
                    class="space-y-4"
                >
                <template #item="{element: section, index: sectionIndex}">
                    <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                        <!-- Section Header -->
                        <div class="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="section-handle cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mr-3">
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                                </svg>
                            </div>
                            <div class="flex-1" @click.stop>
                                <div v-if="section.isEditing" class="space-y-1" @blur.capture="handleSectionBlur(section, $event)">
                                    <input
                                        v-model="section.title"
                                        type="text"
                                        placeholder="Section Title"
                                        class="w-full text-lg font-semibold border-0 border-b-2 border-blue-500 dark:bg-gray-800 dark:text-white focus:outline-none"
                                        @keyup.enter="$event.target.nextElementSibling?.focus() || (section.isEditing = false)"
                                    />
                                    <input
                                        v-model="section.description"
                                        type="text"
                                        placeholder="Section Description (optional)"
                                        class="w-full text-sm text-gray-600 dark:text-gray-400 border-0 dark:bg-gray-800 focus:outline-none"
                                        @keyup.enter="section.isEditing = false"
                                    />
                                </div>
                                <div v-else @click="section.isEditing = true" class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-1 -m-1">
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white hover:text-blue-600">
                                        {{ section.title || 'Untitled Section' }}
                                    </h3>
                                    <p v-if="section.description" class="text-sm text-gray-600 dark:text-gray-400">
                                        {{ section.description }}
                                    </p>
                                </div>
                            </div>
                            <button
                                @click="deleteSection(sectionIndex)"
                                type="button"
                                class="ml-3 text-red-500 hover:text-red-700"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>

                        <!-- Section Fields -->
                        <div class="p-4">
                            <draggable
                                v-model="section.fields"
                                item-key="id"
                                group="fields"
                                @end="onFieldReorder(section)"
                                @add="onFieldAdded(section, $event)"
                                class="min-h-[100px] space-y-2"
                                :class="{'border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4': !section.fields || section.fields.length === 0}"
                            >
                                <template #item="{element: field, index: fieldIndex}">
                                    <div
                                        @click="selectField(field)"
                                        class="flex items-start p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:border-blue-500 cursor-pointer transition-colors"
                                        :class="{'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800': selectedFieldId === field.id}"
                                    >
                                        <div class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mr-3 cursor-move">
                                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center">
                                                <span class="text-sm font-medium text-gray-900 dark:text-white">
                                                    {{ field.label || 'Untitled Field' }}
                                                </span>
                                                <span v-if="isFieldRequired(field)" class="ml-2 text-red-500 text-xs">*</span>
                                                <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                                    ({{ getFieldTypeLabel(field.field_type) }})
                                                </span>
                                            </div>
                                            <div class="flex items-center mt-1 space-x-2 text-xs text-gray-500 dark:text-gray-400">
                                                <span v-if="field.allow_notes || field.require_notes">📝 Notes</span>
                                                <span v-if="field.allow_photos || field.require_photos">📷 Photos</span>
                                                <span v-if="field.allow_attachments || field.require_attachments">📎 Files</span>
                                                <span v-if="field.dependency_rule">🔗 Conditional</span>
                                            </div>
                                        </div>
                                        <button
                                            @click.stop="deleteField(section, fieldIndex)"
                                            type="button"
                                            class="ml-3 text-red-500 hover:text-red-700"
                                        >
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </template>
                                <template #footer>
                                    <div v-if="!section.fields || section.fields.length === 0" class="text-center text-gray-400 dark:text-gray-500 text-sm">
                                        Drag fields here
                                    </div>
                                </template>
                            </draggable>
                        </div>
                    </div>
                </template>
            </draggable>

                <!-- Add Section Button -->
                <button
                    @click="addSection"
                    type="button"
                    class="w-full mt-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors"
                >
                    <svg class="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Section
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import ConfirmModal from './ConfirmModal.vue';
import { StarIcon } from '@heroicons/vue/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/vue/24/outline';

const props = defineProps({
    sections: Array,
    selectedFieldId: [Number, String, null],
    previewMode: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:sections', 'field-selected', 'section-reorder', 'field-reorder', 'field-added']);

const localSections = computed({
    get: () => props.sections,
    set: (value) => emit('update:sections', value),
});

// Preview mode state
const previewValues = ref({});

// Confirm modal state
const confirmModal = ref({
    show: false,
    title: '',
    message: '',
    type: 'danger',
    confirmText: 'Confirm',
    onConfirm: () => {}
});

// Reset preview values when preview mode is toggled
watch(() => props.previewMode, (newVal) => {
    if (newVal) {
        previewValues.value = {};
        // Initialize checkbox fields as arrays
        props.sections?.forEach(section => {
            section.fields?.forEach(field => {
                if (field.field_type === 'multiple_selection') {
                    previewValues.value[field.id] = [];
                }
            });
        });
    }
});

const fieldTypeLabels = {
    yes_no: 'Yes/No',
    pass_fail_na: 'Pass/Fail/NA',
    multiple_choice: 'Multiple Choice',
    multiple_selection: 'Checkboxes',
    dropdown_select: 'Dropdown',
    text_input: 'Short Text',
    textarea: 'Long Text',
    number_input: 'Number',
    decimal_input: 'Decimal',
    rating_scale: 'Rating',
    date_picker: 'Date',
    datetime_picker: 'Date & Time',
    time_picker: 'Time',
    photo_capture: 'Photo',
    file_upload: 'File',
    signature: 'Signature',
};

const getFieldTypeLabel = (type) => fieldTypeLabels[type] || type;

const addSection = () => {
    const newSection = {
        id: Date.now(),
        title: '',
        description: '',
        display_order: localSections.value.length + 1,
        fields: [],
        isEditing: true,
        isNew: true,
    };
    localSections.value.push(newSection);
};

const deleteSection = (index) => {
    confirmModal.value = {
        show: true,
        title: 'Delete Section',
        message: 'Are you sure you want to delete this section and all its fields? This action cannot be undone.',
        type: 'danger',
        confirmText: 'Delete',
        onConfirm: () => {
            localSections.value.splice(index, 1);
            confirmModal.value.show = false;
        }
    };
};

const deleteField = (section, fieldIndex) => {
    confirmModal.value = {
        show: true,
        title: 'Delete Field',
        message: 'Are you sure you want to delete this field? This action cannot be undone.',
        type: 'danger',
        confirmText: 'Delete',
        onConfirm: () => {
            section.fields.splice(fieldIndex, 1);
            confirmModal.value.show = false;
        }
    };
};

const selectField = (field) => {
    emit('field-selected', field);
};

const onSectionReorder = () => {
    localSections.value.forEach((section, index) => {
        section.display_order = index + 1;
    });
    emit('section-reorder');
};

const onFieldReorder = (section) => {
    section.fields.forEach((field, index) => {
        field.sort_order = index + 1;
    });
    emit('field-reorder', section);
};

const onFieldAdded = (section, event) => {
    const field = section.fields[event.newIndex];
    if (field && !field.label) {
        field.section_id = section.id;
        field.sort_order = event.newIndex + 1;
        emit('field-added', field);
        selectField(field);
    }
};

const handleSectionBlur = (section, event) => {
    // Only close editing if focus moved outside the editing container
    setTimeout(() => {
        const currentTarget = event.currentTarget;
        if (currentTarget && !currentTarget.contains(document.activeElement)) {
            section.isEditing = false;
        }
    }, 0);
};

// Helper functions for preview mode
const getFieldConfig = (field) => {
    if (!field.field_config) return {};
    return typeof field.field_config === 'string' ? JSON.parse(field.field_config) : field.field_config;
};

const getFieldOptions = (field) => {
    const config = getFieldConfig(field);
    return config.options || ['Option 1', 'Option 2', 'Option 3'];
};

// Preview mode: Check if a field should be visible based on dependency rules
const isFieldVisible = (field) => {
    if (!props.previewMode) return true;

    // Primary fields are always visible
    if (field.is_primary || !field.dependency_rule) return true;

    const rule = typeof field.dependency_rule === 'string'
        ? JSON.parse(field.dependency_rule)
        : field.dependency_rule;

    if (!rule || !rule.parent_field_id) return true;

    const parentValue = previewValues.value[rule.parent_field_id];

    // If parent hasn't been answered, hide dependent field
    if (parentValue === undefined || parentValue === null || parentValue === '') return false;

    // Check condition
    const normalizedParentValue = String(parentValue).toLowerCase();
    const normalizedConditionValue = String(rule.value).toLowerCase();

    switch (rule.condition) {
        case 'equals':
            return normalizedParentValue === normalizedConditionValue;
        case 'not_equals':
            return normalizedParentValue !== normalizedConditionValue;
        case 'contains':
            return normalizedParentValue.includes(normalizedConditionValue);
        case 'greater_than':
            return parseFloat(parentValue) > parseFloat(rule.value);
        case 'less_than':
            return parseFloat(parentValue) < parseFloat(rule.value);
        default:
            return true;
    }
};

// Preview mode: Get visible fields for a section
const getVisibleFields = (section) => {
    if (!props.previewMode || !section.fields) return section.fields;
    return section.fields.filter(field => isFieldVisible(field));
};

// Preview mode: Check if field is required
// Primary fields: use is_required column
// Dependent fields: use dependency rule action only
const isFieldRequired = (field) => {
    if (!field) return false;

    // Dependent field - use dependency rule action only
    if (field.dependency_rule && field.parent_field_id) {
        const rule = typeof field.dependency_rule === 'string'
            ? JSON.parse(field.dependency_rule)
            : field.dependency_rule;

        return rule?.action === 'show_and_require';
    }

    // Primary field - use is_required column
    return field.is_required === true || field.is_required === 1;
};

// Preview mode: Clear dependent fields recursively
const clearDependentFields = (parentFieldId) => {
    // Find all fields that depend on this parent field
    localSections.value.forEach(section => {
        if (!section.fields) return;

        section.fields.forEach(field => {
            // Check if this field depends on the parent
            if (field.dependency_rule) {
                const rule = typeof field.dependency_rule === 'string'
                    ? JSON.parse(field.dependency_rule)
                    : field.dependency_rule;

                if (rule && rule.parent_field_id === parentFieldId) {
                    // Check if this field should now be hidden
                    if (!isFieldVisible(field)) {
                        // Clear the field value
                        delete previewValues.value[field.id];

                        // Recursively clear any fields that depend on this field
                        clearDependentFields(field.id);
                    }
                }
            }
        });
    });
};

// Preview mode: Update field value
const updateFieldValue = (fieldId, value) => {
    previewValues.value[fieldId] = value;

    // Clear any dependent fields that should now be hidden
    clearDependentFields(fieldId);
};
</script>
